import { Send } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWhatsAppService from '../../services/adminWhatsAppService';
import {
  WHATSAPP_INBOX_REPLY_CODE as CODE,
  WHATSAPP_TEMPLATE_STATUS as STATUS,
  statusLabel,
} from '../../utils/whatsapp';
import { hasDynamicLink } from './TemplateList';
import TemplatePreview from './TemplatePreview';

const REFRESH_CODES = /** @type {string[]} */ ([
  CODE.TEMPLATE_UNAVAILABLE,
  CODE.INVALID_PARAMS,
  CODE.UNSUPPORTED_TEMPLATE,
]);
const FLIP_CODES = /** @type {string[]} */ ([
  CODE.WINDOW_OPEN,
  CODE.WINDOW_CLOSED,
  CODE.SUPPRESSED,
  CODE.SEND_DISABLED,
]);

const firstNameOf = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)[0] || '';

/**
 * Sends an approved template to one number: it takes the place of the composer when the 24 hour
 * window is closed. Only approved templates without a dynamic-URL button are offered. Refusals that
 * change the thread (window_open, suppressed, send_disabled) are handled by the thread itself, so
 * this panel only toasts the rest.
 * @param {{contactName?: string, onSend: (payload: {template_id: string, body_params: string[]}) => Promise<{ok: boolean, code?: string | null, message?: string}>, onCancel: () => void}} props
 */
const TemplateSendPanel = ({ contactName = '', onSend, onCancel }) => {
  const toast = useToast();
  const [templates, setTemplates] = useState(/** @type {any[] | null} */ (null));
  const [loadError, setLoadError] = useState('');
  const [selectedId, setSelectedId] = useState(/** @type {string | null} */ (null));
  const [values, setValues] = useState(/** @type {string[]} */ ([]));
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const loadTicket = useRef(0);

  const load = useCallback(async () => {
    const ticket = ++loadTicket.current;
    setLoadError('');
    try {
      const rows = await adminWhatsAppService.listTemplates({ status: STATUS.APPROVED });
      if (ticket !== loadTicket.current) return;
      setTemplates(rows.filter((t) => t.status === STATUS.APPROVED && !hasDynamicLink(t)));
    } catch (err) {
      if (ticket !== loadTicket.current) return;
      setLoadError(err.message);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      loadTicket.current += 1;
    };
  }, [load]);

  const selected = (templates || []).find((t) => t.id === selectedId) || null;
  const count = selected ? selected.variable_count || 0 : 0;
  const filled = values.slice(0, count);
  const ready = !!selected && filled.length === count && filled.every((v) => String(v ?? '').trim() !== '');

  const choose = (template) => {
    const n = template.variable_count || 0;
    const initial = Array.from({ length: n }, () => '');
    if (n > 0) initial[0] = firstNameOf(contactName);
    setSelectedId(template.id);
    setValues(initial);
  };

  const setValue = (index, value) => setValues((prev) => prev.map((v, i) => (i === index ? value : v)));

  const submit = async () => {
    if (sendingRef.current || !ready) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const result = await onSend({ template_id: selected.id, body_params: filled.map((v) => v.trim()) });
      if (result.ok) {
        onCancel();
        return;
      }
      if (REFRESH_CODES.includes(result.code)) {
        toast.error(result.message || 'That template cannot be sent');
        setSelectedId(null);
        load();
      } else if (!FLIP_CODES.includes(result.code)) {
        toast.error(result.message || 'Could not send that template');
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const marketing = selected?.category === 'marketing';

  return (
    <div className="max-h-[80vh] overflow-y-auto border-t border-border bg-card px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-medium text-foreground">Send a template</p>
        <button type="button" onClick={onCancel} disabled={sending} className="px-2 py-1 text-sm text-muted-foreground">
          Cancel
        </button>
      </div>

      {loadError ? (
        <div className="flex items-center gap-3">
          <p className="text-sm text-foreground/80">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="px-3 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Try again
          </button>
        </div>
      ) : templates === null ? (
        <p className="text-sm text-muted-foreground">Loading templates...</p>
      ) : templates.length === 0 ? (
        <p className="text-sm text-muted-foreground">No approved templates can be sent here yet.</p>
      ) : (
        <>
          <ul aria-label="Approved templates" className="mb-3 flex flex-wrap gap-2">
            {templates.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  data-testid={`template-option-${t.name}`}
                  aria-pressed={t.id === selectedId}
                  onClick={() => choose(t)}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm ${
                    t.id === selectedId
                      ? 'border-foreground ring-1 ring-foreground'
                      : 'border-border hover:border-muted-foreground'
                  }`}
                >
                  <span className="font-medium text-foreground">{t.name}</span>
                  <span className="rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs text-foreground/80">
                    {statusLabel(t.category)}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {selected && (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3">
                {marketing && (
                  <p
                    role="note"
                    className="rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/50 px-3 py-2 text-sm text-amber-800 dark:text-amber-300"
                  >
                    Marketing: needs the customer&apos;s opt-in. Only send it if they agreed to hear from you.
                  </p>
                )}
                {count === 0 && <p className="text-sm text-muted-foreground">This template has no variables.</p>}
                {filled.map((value, index) => (
                  <label key={`${selected.id}-${index}`} className="block text-sm text-foreground/80">
                    {`Variable ${index + 1}`}
                    <input
                      type="text"
                      data-testid={`template-var-${index + 1}`}
                      value={value}
                      onChange={(e) => setValue(index, e.target.value)}
                      className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-base"
                    />
                  </label>
                ))}
                <button
                  type="button"
                  onClick={submit}
                  disabled={!ready || sending}
                  className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
                >
                  <Send size={16} /> Send template
                </button>
              </div>
              <TemplatePreview template={selected} params={filled} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TemplateSendPanel;
